import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/motion";
import { StatusBadge } from "@/pages/OrderPage";

const EMPTY_PRODUCT = { name: "", category: "OTC Health", rx: false, price: "", mrp: "", pack: "", composition: "", description: "", image: "", stock: 100, batch: "", featured: false };
const CATEGORIES = ["Prescription Rx", "Cardiac Care", "OTC Health", "Syrups & Tonics", "Supplements", "Medical Devices"];
const STATUSES = ["processing", "shipped", "delivered", "cancelled"];
const FIELD = "w-full border hairline bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#0F5A47] transition-colors";

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [refForm, setRefForm] = useState({ code: "", rep_name: "" });
  const [refSaving, setRefSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} product draft
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/referrals").then((r) => setReferrals(r.data)).catch(() => {});
  };

  const createReferral = async () => {
    setRefSaving(true);
    try {
      await api.post("/admin/referrals", refForm);
      toast.success(`Referral code ${refForm.code.toUpperCase()} created`);
      setRefForm({ code: "", rep_name: "" });
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefSaving(false);
    }
  };

  const toggleReferral = async (id) => {
    try {
      const { data } = await api.patch(`/admin/referrals/${id}`);
      toast.success(`${data.code} ${data.active ? "activated" : "deactivated"}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin]);

  if (user === null) return <p className="pt-40 text-center font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Loading…</p>;
  if (!isAdmin) return (
    <div className="pt-40 pb-32 text-center" data-testid="admin-denied">
      <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Admin access required</p>
      <Link to="/login" className="text-[#0F5A47] font-mono-ed text-xs uppercase tracking-[0.2em] mt-4 inline-block">Sign in →</Link>
    </div>
  );

  const saveProduct = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, price: Number(editing.price), mrp: editing.mrp ? Number(editing.mrp) : null, stock: Number(editing.stock) };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing.id ? "Product updated" : "Product created");
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success(`Order marked ${status}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const TabBtn = ({ id, label, testid }) => (
    <button
      data-testid={testid}
      onClick={() => setTab(id)}
      className={`px-5 py-2.5 font-mono-ed text-[10px] uppercase tracking-[0.2em] border transition-colors ${tab === id ? "bg-[#0B0F17] text-white border-[#0B0F17]" : "bg-white text-slate-500 hairline hover:border-[#0F5A47]"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-28 pb-20" data-testid="admin-panel">
      <Reveal>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#1D4ED8] mb-3">Kanan Control Room</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Admin</h1>
      </Reveal>

      <div className="flex gap-2 mt-10 mb-10 flex-wrap">
        <TabBtn id="overview" label="Overview" testid="admin-tab-overview" />
        <TabBtn id="products" label="Products" testid="admin-tab-products" />
        <TabBtn id="orders" label="Orders" testid="admin-tab-orders" />
        <TabBtn id="enquiries" label="Enquiries" testid="admin-tab-enquiries" />
        <TabBtn id="referrals" label="Referrals" testid="admin-tab-referrals" />
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border hairline" data-testid="admin-stats-grid">
          {[
            ["Total Revenue", inr(stats.total_revenue)],
            ["Paid Orders", stats.paid_orders],
            ["Pending Details", stats.pending_dl_gst],
            ["Rx Orders", stats.rx_orders],
            ["Products", stats.total_products],
            ["Customers", stats.total_customers],
          ].map(([label, value]) => (
            <div key={label} className="bg-white p-8">
              <p className="font-mono-ed text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</p>
              <p className="font-display font-extrabold text-3xl mt-3" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div>
          <button
            data-testid="admin-product-create-btn"
            onClick={() => setEditing({ ...EMPTY_PRODUCT })}
            className="flex items-center gap-2 bg-[#0F5A47] text-white px-6 py-3 font-mono-ed text-xs uppercase tracking-[0.2em] hover:bg-[#0A4234] transition-colors active:scale-95 mb-6"
          >
            <Plus size={14} /> New Product
          </button>
          <div className="border hairline bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b hairline font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400 text-left">
                  <th className="px-5 py-3">Product</th><th className="px-5 py-3">Category</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Net Rate</th><th className="px-5 py-3">Stock</th><th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b hairline last:border-0 hover:bg-[#FAFBFD]" data-testid={`admin-product-row-${p.id}`}>
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    <td className="px-5 py-3 text-slate-500">{p.category}</td>
                    <td className="px-5 py-3">{p.rx ? <span className="font-mono-ed text-[9px] text-red-700 border border-red-300 px-1.5 py-0.5">Rx</span> : <span className="font-mono-ed text-[9px] text-emerald-700 border border-emerald-300 px-1.5 py-0.5">OTC</span>}</td>
                    <td className="px-5 py-3 font-mono-ed">{inr(p.price)}</td>
                    <td className="px-5 py-3 font-mono-ed">{p.stock}</td>
                    <td className="px-5 py-3">
                      <div className="flex gap-2 justify-end">
                        <button data-testid={`admin-product-edit-${p.id}`} onClick={() => setEditing({ ...p })} className="p-2 text-slate-400 hover:text-[#1D4ED8] transition-colors"><Pencil size={14} /></button>
                        <button data-testid={`admin-product-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="border hairline bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b hairline font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400 text-left">
                <th className="px-5 py-3">Order</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Total</th><th className="px-5 py-3">Compliance</th><th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-16 text-center font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="admin-orders-empty">No orders yet</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="border-b hairline last:border-0 hover:bg-[#FAFBFD]" data-testid={`admin-order-row-${o.order_number}`}>
                  <td className="px-5 py-3 font-mono-ed font-semibold">{o.order_number}</td>
                  <td className="px-5 py-3">
                    <p className="font-medium">{o.customer.business_name || o.customer.name}</p>
                    <p className="text-xs text-slate-400">{o.customer.business_name ? `${o.customer.name} · ` : ""}{o.customer.phone}{o.customer.email ? ` · ${o.customer.email}` : ""}</p>
                    {o.customer.address
                      ? <p className="text-xs text-slate-500 mt-1 max-w-[220px]">{o.customer.address}, {o.customer.city}, {o.customer.state} — {o.customer.pincode}</p>
                      : <p className="text-xs text-amber-700 mt-1">Address pending</p>}
                  </td>
                  <td className="px-5 py-3 font-mono-ed text-[10px] uppercase">{o.buyer_type}{o.has_rx ? " · Rx" : ""}</td>
                  <td className="px-5 py-3 font-mono-ed">{inr(o.total)}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1">
                      {o.prescription_path && (
                        <a data-testid={`admin-rx-view-${o.order_number}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${o.prescription_path}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#0F5A47] text-xs hover:underline"><FileText size={12} /> Rx file</a>
                      )}
                      {o.dl_number && <span className="font-mono-ed text-[10px] text-slate-500">DL {o.dl_number}</span>}
                      {o.gst_number && <span className="font-mono-ed text-[10px] text-slate-500">GST {o.gst_number}</span>}
                      {o.referral_code && <span className="font-mono-ed text-[10px] text-emerald-700" data-testid={`admin-order-referral-${o.order_number}`}>REF {o.referral_code} · −{inr(o.discount_amount || 0)}</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={o.status} />
                      <select
                        data-testid={`admin-order-status-${o.order_number}`}
                        value=""
                        onChange={(e) => e.target.value && updateStatus(o.id, e.target.value)}
                        className="border hairline text-xs px-2 py-1.5 bg-white focus:outline-none"
                      >
                        <option value="">Update…</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "referrals" && (
        <div data-testid="admin-referrals-section">
          <div className="border hairline bg-white p-6 mb-6">
            <p className="font-mono-ed text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-4">Create referral code (buyer gets 3% extra off Net Rate)</p>
            <div className="flex flex-wrap gap-3">
              <input data-testid="admin-referral-code-input" className={`${FIELD} max-w-[220px] uppercase`} placeholder="Code e.g. RAHUL8" value={refForm.code} onChange={(e) => setRefForm({ ...refForm, code: e.target.value })} />
              <input data-testid="admin-referral-rep-input" className={`${FIELD} max-w-[220px]`} placeholder="Salesperson name" value={refForm.rep_name} onChange={(e) => setRefForm({ ...refForm, rep_name: e.target.value })} />
              <button data-testid="admin-referral-create-btn" onClick={createReferral} disabled={refSaving || !refForm.code.trim() || !refForm.rep_name.trim()} className="flex items-center gap-2 bg-[#0F5A47] text-white px-6 py-3 font-mono-ed text-xs uppercase tracking-[0.2em] hover:bg-[#0A4234] transition-colors active:scale-95 disabled:opacity-40">
                {refSaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Code
              </button>
            </div>
          </div>
          <div className="border hairline bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b hairline font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400 text-left">
                  <th className="px-5 py-3">Code</th><th className="px-5 py-3">Salesperson</th><th className="px-5 py-3">Orders</th><th className="px-5 py-3">Sales Value</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-16 text-center font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="admin-referrals-empty">No referral codes yet</td></tr>
                )}
                {referrals.map((r) => (
                  <tr key={r.id} className="border-b hairline last:border-0 hover:bg-[#FAFBFD]" data-testid={`admin-referral-row-${r.code}`}>
                    <td className="px-5 py-3 font-mono-ed font-bold">{r.code}</td>
                    <td className="px-5 py-3 font-medium">{r.rep_name}</td>
                    <td className="px-5 py-3 font-mono-ed" data-testid={`admin-referral-orders-${r.code}`}>{r.orders}</td>
                    <td className="px-5 py-3 font-mono-ed" data-testid={`admin-referral-revenue-${r.code}`}>{inr(r.revenue)}</td>
                    <td className="px-5 py-3">{r.active
                      ? <span className="font-mono-ed text-[10px] uppercase text-emerald-700 border border-emerald-300 bg-emerald-50 px-2 py-0.5">Active</span>
                      : <span className="font-mono-ed text-[10px] uppercase text-red-700 border border-red-300 bg-red-50 px-2 py-0.5">Inactive</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button data-testid={`admin-referral-toggle-${r.code}`} onClick={() => toggleReferral(r.id)} className="font-mono-ed text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:text-[#1D4ED8] transition-colors">
                        {r.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "enquiries" && (        <div className="border hairline bg-white divide-y divide-[#E2E8F0]" data-testid="admin-enquiries-list">
          {enquiries.length === 0 && <p className="px-5 py-16 text-center font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="admin-enquiries-empty">No enquiries yet</p>}
          {enquiries.map((q) => (
            <div key={q.id} className="px-6 py-5 hover:bg-[#FAFBFD] transition-colors" data-testid={`admin-enquiry-row-${q.id}`}>
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-semibold text-sm">{q.name} {q.business && <span className="text-slate-400 font-normal">· {q.business}</span>}</p>
                <span className="font-mono-ed text-[10px] text-slate-400">{new Date(q.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <p className="font-mono-ed text-xs text-slate-500 mt-1">{q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{q.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* PRODUCT EDITOR MODAL */}
      {editing && (
        <div className="fixed inset-0 z-[90] bg-[#0B0F17]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-editor-modal">
          <div className="bg-white border hairline w-full max-w-2xl max-h-[88vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-xl">{editing.id ? "Edit Product" : "New Product"}</h3>
              <button data-testid="product-editor-close" onClick={() => setEditing(null)} className="p-2 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="pe-name" className={FIELD} placeholder="Product name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <select data-testid="pe-category" className={FIELD} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input data-testid="pe-price" className={FIELD} type="number" placeholder="Net Rate / PTR (₹)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              <input data-testid="pe-mrp" className={FIELD} type="number" placeholder="MRP (₹)" value={editing.mrp || ""} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })} />
              <input data-testid="pe-pack" className={FIELD} placeholder="Pack e.g. Strip of 10 tablets" value={editing.pack} onChange={(e) => setEditing({ ...editing, pack: e.target.value })} />
              <input data-testid="pe-batch" className={FIELD} placeholder="Batch code" value={editing.batch} onChange={(e) => setEditing({ ...editing, batch: e.target.value })} />
              <input data-testid="pe-stock" className={FIELD} type="number" placeholder="Stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} />
              <input data-testid="pe-image" className={FIELD} placeholder="Image URL" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} />
              <input data-testid="pe-composition" className={`${FIELD} sm:col-span-2`} placeholder="Composition" value={editing.composition} onChange={(e) => setEditing({ ...editing, composition: e.target.value })} />
              <textarea data-testid="pe-description" className={`${FIELD} sm:col-span-2`} rows={3} placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="pe-rx" type="checkbox" checked={editing.rx} onChange={(e) => setEditing({ ...editing, rx: e.target.checked })} /> Prescription (Rx)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="pe-featured" type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured on home
              </label>
            </div>
            <button
              data-testid="pe-save-btn"
              onClick={saveProduct}
              disabled={saving || !editing.name || !editing.price}
              className="mt-6 bg-[#0F5A47] text-white px-10 py-3.5 font-mono-ed text-xs uppercase tracking-[0.25em] hover:bg-[#0A4234] transition-colors active:scale-95 disabled:opacity-40 flex items-center gap-2"
            >
              {saving && <Loader2 size={13} className="animate-spin" />} Save Product
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
